import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Scan, Barcode } from "@phosphor-icons/react";
import { toast } from "sonner";

export function AssetScanDialog({ open, onClose }) {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [camError, setCamError] = useState("");
  const scannerRef = useRef(null);

  const resolve = async (raw) => {
    if (!raw || busy) return;
    setBusy(true);
    try {
      const { data } = await API.get("/assets/lookup", { params: { code: raw } });
      toast.success(`Found ${data.name}`);
      onClose();
      navigate(`/assets/${data.id}`);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Tag not recognised"); } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setCamError("");
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (cancelled) return;
        const el = document.getElementById("asset-scan-region");
        if (!el) return;
        const scanner = new Html5Qrcode("asset-scan-region", { verbose: false });
        scannerRef.current = scanner;
        await scanner.start({ facingMode: "environment" }, { fps: 8, qrbox: { width: 220, height: 220 } }, (text) => {
          scanner.pause(true);
          resolve(text).finally(() => { try { scanner.resume(); } catch { /* closed */ } });
        }, () => {});
      } catch (err) {
        if (!cancelled) setCamError("Camera unavailable — type the tag code instead.");
      }
    })();
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) s.stop().then(() => s.clear()).catch(() => {});
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="asset-scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo] flex items-center gap-2"><Scan size={18} /> Scan an asset tag</DialogTitle>
          <DialogDescription>Point the camera at a system QR label or any existing barcode / QR tag you've registered.</DialogDescription>
        </DialogHeader>
        <div id="asset-scan-region" className="rounded-lg overflow-hidden bg-black/80 min-h-[200px] [&_video]:w-full" data-testid="asset-scan-region" />
        {camError && <p className="text-xs text-amber-600" data-testid="asset-scan-cam-error">{camError}</p>}
        <form onSubmit={(e) => { e.preventDefault(); resolve(code.trim()); }} className="flex gap-2">
          <div className="relative flex-1">
            <Barcode size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Or type the tag code…" className="pl-9 font-mono uppercase" data-testid="asset-scan-input" autoFocus />
          </div>
          <Button type="submit" disabled={busy || !code.trim()} data-testid="asset-scan-submit">{busy ? "Looking…" : "Find"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
